import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}



/* =========================================================
   AMORE SWISS — BARRA DE ENVÍO GRATIS
   Meta: $250 MXN
========================================================= */

(() => {

  const threshold = 25000; // $250 MXN

  let updateTimeout = null;
  let isUpdating = false;


  /* ================================
     FORMATO DE DINERO
  ================================= */

  function formatMoney(cents) {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2
    }).format(cents / 100);
  }


  /* ================================
     ENCONTRAR EL CART DRAWER
  ================================= */

  function getDrawer() {
    return document.querySelector(
      'cart-drawer-component, cart-drawer, .cart-drawer, #CartDrawer, [id*="CartDrawer"]'
    );
  }


  /* ================================
     CREAR / ENCONTRAR BARRA
  ================================= */

  function ensureBar() {

    const drawer = getDrawer();

    if (!drawer) return null;


    /* Si ya existe, utilizarla */

    let bar = drawer.querySelector(
      '.amore-cart-progress-js'
    );

    if (bar) return bar;


    /*
      Buscar primero el contenido principal.
      Esto hace que la barra aparezca ARRIBA
      del listado de productos.
    */

    const content =
      drawer.querySelector(
        '.cart-drawer__content'
      );


    /*
      Como respaldo buscamos footer/summary.
    */

    const destination =
      content ||
      drawer.querySelector(
        '.cart-drawer__summary'
      ) ||
      drawer.querySelector(
        '.cart-drawer__footer'
      ) ||
      drawer.querySelector(
        'form[action="/cart"]'
      );


    if (!destination) return null;


    bar = document.createElement('div');

    bar.className =
      'amore-cart-progress-js';


    /*
      Lo colocamos al inicio.
    */

    destination.prepend(bar);

    return bar;
  }


  /* ================================
     DIBUJAR BARRA
  ================================= */

  function renderBar(bar, cart) {

    const total =
      Number(cart.total_price || 0);

    const remaining =
      Math.max(
        threshold - total,
        0
      );

    const progress =
      Math.min(
        Math.max(
          (total / threshold) * 100,
          0
        ),
        100
      );


    let message;


    if (total >= threshold) {

      message =
        '🎁 ¡Ya tienes envío gratis!';

    }

    else if (total > 0) {

      message =
        `Te faltan <strong>${formatMoney(remaining)}</strong> para envío gratis`;

    }

    else {

      message =
        `Obtén <strong>envío gratis</strong> en compras mayores a $250 MXN`;

    }


    bar.innerHTML = `
      <p>
        ${message}
      </p>

      <div class="amore-progress-bar-js">
        <span style="width:${progress}%"></span>
      </div>
    `;

  }


  /* ================================
     CONSULTAR CARRITO REAL
  ================================= */

  async function updateFreeShippingBar() {

    if (isUpdating) return;

    isUpdating = true;

    try {

      const root =
        window.Shopify?.routes?.root || '/';


      const response =
        await fetch(
          `${root}cart.js?t=${Date.now()}`,
          {
            method: 'GET',

            headers: {
              Accept:
                'application/json'
            },

            cache: 'no-store'
          }
        );


      if (!response.ok) {
        throw new Error(
          'No se pudo obtener el carrito'
        );
      }


      const cart =
        await response.json();


      const bar =
        ensureBar();


      if (!bar) return;


      renderBar(
        bar,
        cart
      );

    }

    catch (error) {

      console.warn(
        'Amore Swiss free shipping:',
        error
      );

    }

    finally {

      isUpdating = false;

    }

  }


  /* ================================
     PROGRAMAR ACTUALIZACIÓN
  ================================= */

  function scheduleUpdate(delay = 50) {

    clearTimeout(
      updateTimeout
    );


    updateTimeout =
      setTimeout(
        updateFreeShippingBar,
        delay
      );

  }


  /* ================================
     CUANDO SE ABRE EL DRAWER
  ================================= */

  document.addEventListener(
    DialogOpenEvent.eventName,
    () => {

      /*
        Primera actualización inmediata
      */

      updateFreeShippingBar();


      /*
        Horizon puede terminar de renderizar
        después de abrir el drawer.
      */

      setTimeout(
        updateFreeShippingBar,
        100
      );

      setTimeout(
        updateFreeShippingBar,
        300
      );

    }
  );


  /* ================================
     CUANDO SE AGREGA PRODUCTO
     EVENTO NATIVO DE HORIZON
  ================================= */

  document.addEventListener(
    CartAddEvent.eventName,
    () => {

      setTimeout(
        updateFreeShippingBar,
        50
      );

      setTimeout(
        updateFreeShippingBar,
        250
      );

      setTimeout(
        updateFreeShippingBar,
        500
      );

    }
  );


  /* ================================
     OBSERVAR CAMBIOS DEL DRAWER
  ================================= */

  const observer =
    new MutationObserver(
      (mutations) => {

        const drawer =
          getDrawer();


        if (!drawer) return;


        let drawerChanged =
          false;


        for (
          const mutation of mutations
        ) {

          if (
            drawer.contains(
              mutation.target
            )
          ) {

            /*
              Ignorar cambios realizados
              por nuestra propia barra.
            */

            if (
              mutation.target.closest?.(
                '.amore-cart-progress-js'
              )
            ) {

              continue;

            }


            drawerChanged =
              true;

            break;

          }

        }


        if (drawerChanged) {

          scheduleUpdate(
            100
          );

        }

      }
    );


  observer.observe(
    document.body,
    {
      childList: true,
      subtree: true
    }
  );


  /* ================================
     BOTONES + / - / ELIMINAR
  ================================= */

  document.addEventListener(
    'click',
    (event) => {

      const drawer =
        getDrawer();


      if (
        !drawer ||
        !drawer.contains(
          event.target
        )
      ) {

        return;

      }


      const control =
        event.target.closest(
          'button, [role="button"], a'
        );


      if (!control) return;


      /*
        Dejamos que Shopify cambie primero
        la cantidad.
      */

      setTimeout(
        updateFreeShippingBar,
        150
      );

      setTimeout(
        updateFreeShippingBar,
        400
      );

      setTimeout(
        updateFreeShippingBar,
        700
      );

    },
    true
  );


  /* ================================
     PRIMERA CARGA
  ================================= */

  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      () => {

        updateFreeShippingBar();

      }
    );

  }

  else {

    updateFreeShippingBar();

  }


  /*
    Disponible desde consola
    para pruebas.
  */

  window.amoreUpdateFreeShipping =
    updateFreeShippingBar;

})();